// ─────────────────────────────────────────────────────────────────────────────
// Modal "Editar Insignia" de la sección Insignias de Campaña (Ganadores). Mismo patrón que
// AdminBadgeEditorModal (Gestión/Streak): LocalizedFields + traductor, CatalogImageUploadField,
// imagen deshabilitada hasta el primer guardado. Se diferencia en que NO hay semanas/color —insignia
// de campaña no tiene umbral de racha— y sí un campo Año/Subtítulo de texto libre (×7 idiomas), y en
// que el guardado NO es un draft-hasta-el-Guardar-general: cada acción (crear/editar/eliminar/subir
// imagen) persiste al instante contra su propio endpoint (Task 2.3) — no hay botón "Guardar" a nivel
// de sección porque el mockup no lo pide.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, CatalogImageUploadField } from '@/components/common';
import { useUploadCampaignBadgeImage, getRecognitionErrorMessage, getBadgeName } from '@/api/recognition';
import type { AdminBadgeDto, LocalizedText } from '@/api/recognition';
import { LocalizedFields, aniosDisponibles } from './adminShared';
import { asset } from '@/utils/basePath';

const TITLE_ID = 'adm-campaign-badge-editor-title';

interface AdminCampaignBadgeEditorModalProps {
  badge: AdminBadgeDto | null;
  /** true si la insignia todavía no pasó por su primer guardado (sin id persistido en el catálogo). */
  isNew: boolean;
  onSave: (badge: AdminBadgeDto) => void;
  /** Se dispara tras una carga de imagen exitosa — actualiza el draft del padre SIN cerrar el modal. */
  onIconUploaded: (badge: AdminBadgeDto) => void;
  onClose: () => void;
  /**
   * Mensaje del último intento de guardado (crear/editar) fallido, ya formateado, o `null`/`undefined`
   * si no hay ninguno pendiente de mostrar. Viene del padre (la mutación vive en `AdminWinners.tsx`)
   * porque `guardarInsignia` deja el modal ABIERTO a propósito cuando el guardado falla, para no
   * perder lo ya tecleado — y por eso el aviso DEBE pintarse aquí adentro, no como banner de página:
   * el overlay del modal (pantalla completa en mobile, viewport completo en desktop) lo taparía por
   * completo y quedaría en el DOM sin que nadie lo vea. Mismo patrón visual que `uploadError`.
   */
  saveError?: string | null;
}

export function AdminCampaignBadgeEditorModal({
  badge, isNew, onSave, onIconUploaded, onClose, saveError,
}: Readonly<AdminCampaignBadgeEditorModalProps>) {
  const { t, i18n } = useTranslation('recognition');
  const uploadImage = useUploadCampaignBadgeImage();
  const [draft, setDraft] = useState<AdminBadgeDto | null>(badge);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (draft?.id !== badge?.id) setDraft(badge);
  if (!draft) return null;

  const patch = (patchValues: Partial<AdminBadgeDto>) =>
    setDraft((current) => (current ? { ...current, ...patchValues } : current));

  const handleUpload = async (file: File) => {
    setUploadError(null);
    try {
      const updated = await uploadImage.mutateAsync({ badgeId: draft.id, file });
      patch({ iconUrl: updated.iconUrl });
      onIconUploaded({ ...draft, iconUrl: updated.iconUrl });
    } catch (error) {
      setUploadError(getRecognitionErrorMessage(error, t('admin.winners.campaignBadges.imageFormatInvalid')));
    }
  };

  return (
    <Modal open={badge !== null} onClose={onClose} maxWidth={640} labelledBy={TITLE_ID}>
      <ModalHeader onClose={onClose}>
        <h2 id={TITLE_ID}>{t('admin.winners.campaignBadges.editTitle')} — {draft.code}</h2>
      </ModalHeader>
      <ModalBody>
        <LocalizedFields
          label={t('admin.winners.campaignBadges.fieldTitle')}
          value={draft.nombre}
          onChange={(nombre: LocalizedText) => patch({ nombre })}
          requireAll
          translatable
        />

        <LocalizedFields
          label={t('admin.winners.campaignBadges.fieldYearSubtitle')}
          value={draft.anioSubtitulo ?? {}}
          onChange={(anioSubtitulo: LocalizedText) => patch({ anioSubtitulo })}
          translatable
        />

        {/* Fila 40 — el año COMO DATO, aparte del rótulo de arriba.
            Son dos campos y no uno a propósito: el de arriba es texto libre ×7 idiomas y puede no ser
            un año ("Innovación LATAM"), así que no se puede leer como dato de negocio. Este sí, y es
            el que impide otorgar la medalla de 2024 en una campaña de 2026.
            "Sin año" es una opción legítima y es el default: una insignia que no es de una edición
            concreta no debe empezar a rechazar altas por sorpresa. */}
        <label className="adm-field"><span>{t('admin.winners.campaignBadges.fieldYear')}</span>
          <select
            value={draft.anio ?? ''}
            onChange={(event) => patch({ anio: event.target.value ? Number(event.target.value) : null })}
          >
            <option value="">{t('admin.winners.campaignBadges.yearNone')}</option>
            {aniosDisponibles().map((anio) => <option key={anio} value={anio}>{anio}</option>)}
          </select>
        </label>
        <p className="adm-note">{t('admin.winners.campaignBadges.fieldYearHint')}</p>

        <CatalogImageUploadField
          label={t('admin.winners.campaignBadges.fieldImage')}
          currentImageUrl={draft.iconUrl ?? undefined}
          onUpload={handleUpload}
          isUploading={uploadImage.isPending}
          disabled={isNew}
        />
        {isNew && <p className="adm-note">{t('admin.winners.campaignBadges.imageUploadHintNew')}</p>}
        {uploadError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {uploadError}</p>}

        <div className="adm-badge-preview">
          <p className="adm-badge-preview-title">{t('admin.winners.campaignBadges.previewTitle')}</p>
          <div className="rp2-insignia-item">
            <img src={draft.iconUrl || asset('/color-icon.png')} alt="" className="rp2-insignia-img" />
            <div className="rp2-insignia-meta">
              <span className="rp2-insignia-name">{getBadgeName(draft, i18n.language)}</span>
            </div>
          </div>
        </div>
        {saveError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {saveError}</p>}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" onClick={() => onSave(draft)}>
          <i className="fas fa-check" /> {t('admin.save')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default AdminCampaignBadgeEditorModal;
